// Presentation-side helpers for the Financial Analytics screen. The heavy
// aggregation happens in FinancialService on the backend; this file only
// derives what the charts need from that response.

import type { FinanceBudgetHeadTotal, FinancePieSlice, FinanceSource } from '../../types';
import { currencySymbol } from '../../utils/format';

export const PERIODS = ['Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'] as const;

export type Period = (typeof PERIODS)[number];

/** "2025-2026" for any date on or after 1 April — mirrors FinancialService. */
export const financialYearOf = (date: Date): string => {
  const year = date.getFullYear();
  return date.getMonth() + 1 >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

/**
 * FY options for the selector: the two preceding years, the current one, and
 * the next. The old screen hardcoded 2024-2027, which silently went stale.
 */
export const financialYearOptions = (today: Date = new Date()): string[] => {
  const current = Number(financialYearOf(today).split('-')[0]);
  return [-2, -1, 0, 1].map((offset) => {
    const start = current + offset;
    return `${start}-${start + 1}`;
  });
};

// --- Range presets -----------------------------------------------------------
// "Financial year" is the default; the rest are quick spans ending today, and
// "Custom" opens two date fields. A preset is resolved to from/to at request
// time so a screen left open overnight still asks for the right days.

export const RANGE_PRESETS = [
  { key: 'fy', label: 'Financial year' },
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'Last 3 months' },
  { key: '90d', label: 'Last 90 days' },
  { key: '12m', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom…' }
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number]['key'];

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** from/to for a preset, or undefined for the FY (the server takes finYear then). */
export const presetRange = (
  preset: RangePreset,
  custom: { from: string; to: string },
  today: Date = new Date()
): { from: string; to: string } | undefined => {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(end);
  switch (preset) {
    case 'month':
      start.setDate(1);
      return { from: iso(start), to: iso(end) };
    case 'quarter':
      start.setMonth(start.getMonth() - 3);
      start.setDate(start.getDate() + 1);
      return { from: iso(start), to: iso(end) };
    case '90d':
      start.setDate(start.getDate() - 89);
      return { from: iso(start), to: iso(end) };
    case '12m':
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
      return { from: iso(start), to: iso(end) };
    case 'custom':
      return custom.from && custom.to && custom.to >= custom.from ? custom : undefined;
    default:
      return undefined;
  }
};

// --- Optional money sources ---------------------------------------------------

export const SOURCE_OPTIONS: ReadonlyArray<{ key: FinanceSource; label: string; kind: 'income' | 'expense'; hint: string }> = [
  { key: 'statutory', label: 'Statutory bills', kind: 'expense', hint: 'Payments recorded under Statutory Payments' },
  // Sunday School offerings are mirrored into Donations on the server, so they
  // are already in "Donations" — no separate toggle any more.
  { key: 'transactions', label: 'Budget ledger', kind: 'income', hint: 'Income/Expense entries in Budget Management' }
];

const SOURCES_KEY = 'church_erp_finance_sources';

/** The toggles are a per-browser preference: remembered, defaulting to all on. */
export const readSources = (): FinanceSource[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(SOURCES_KEY) || 'null');
    if (Array.isArray(stored)) {
      return SOURCE_OPTIONS.map((o) => o.key).filter((k) => stored.includes(k));
    }
  } catch { /* fall through */ }
  return SOURCE_OPTIONS.map((o) => o.key);
};

export const rememberSources = (sources: FinanceSource[]): void => {
  try { localStorage.setItem(SOURCES_KEY, JSON.stringify(sources)); } catch { /* private browsing */ }
};

// --- Figures ------------------------------------------------------------------

export const pieTotal = (pieData: FinancePieSlice[]): number =>
  pieData.reduce((sum, slice) => sum + (slice.value || 0), 0);

/** Spent as a share of allotted, clamped to 100 so the bar cannot overflow. */
export const spentPercent = (head: FinanceBudgetHeadTotal): number => {
  if (!head.allotted) return 0;
  return Math.min((head.spent / head.allotted) * 100, 100);
};

/** True when there is nothing meaningful to plot for the selected year. */
export const hasNoBudgetData = (budgetHeadsData: FinanceBudgetHeadTotal[]): boolean =>
  budgetHeadsData.length === 0 || budgetHeadsData.every((b) => !b.allotted);

/** Traffic light for a budget head: how much of the allotment is used. */
export type BudgetHealth = 'ok' | 'warning' | 'over' | 'none';
export const budgetHealth = (head: FinanceBudgetHeadTotal): BudgetHealth => {
  if (!head.allotted) return head.spent > 0 ? 'over' : 'none';
  const pct = (head.spent / head.allotted) * 100;
  return pct > 100 ? 'over' : pct >= 85 ? 'warning' : 'ok';
};

/**
 * Change against the previous period, as a signed percentage. Null when the
 * previous figure is zero — "up 100%" from nothing says nothing useful.
 */
export const percentChange = (current: number, previous?: number): number | null => {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

/** Axis tick: 12500 -> "₹13k", 250000 -> "₹2.5L" (lakhs only for rupees). */
export const compactAxisTick = (value: number): string => {
  const sym = currencySymbol();
  const abs = Math.abs(value);
  if (sym === '₹' && abs >= 100000) return `${sym}${(value / 100000).toFixed(abs % 100000 === 0 ? 0 : 1)}L`;
  if (abs >= 1000000) return `${sym}${(value / 1000000).toFixed(abs % 1000000 === 0 ? 0 : 1)}M`;
  if (abs >= 1000) return `${sym}${Math.round(value / 1000)}k`;
  return `${sym}${value}`;
};

/** A dozen slices before a colour repeats — enough for any parish's categories. */
export const SLICE_COLORS = [
  '#00ACC1', '#E91E63', '#7C4DFF', '#FF8A00', '#2E7D32', '#8D6E63',
  '#3949AB', '#00897B', '#C0CA33', '#F4511E', '#6D4C41', '#546E7A'
];
